/**
 * Settings > Your agents: how the person wants the agents that work for them to behave. Each policy shows where what
 * applies comes from (the agents' default, the firm's default, or theirs), and one the firm locked cannot be changed.
 * A preference Diggens learned from how they work waits above them as a suggestion, until they use it or say not now.
 */
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@diggens/api-client';
import { POLICY_KEYS, POLICY_KEYS_ADDING_TO_FIRM, type MyPolicies, type MyPolicy, type PolicyKey, type PolicyValues, type ProposalCard as Proposal } from '@diggens/types';
import { PolicyCard, describePolicy } from './PolicyFields';
import { ProposalCard } from './ProposalCard';

export const MY_POLICIES_QUERY_KEY = ['policies', 'me'] as const;
export const MY_PROPOSAL_QUERY_KEY = ['policies', 'proposals'] as const;

/** A key whose value adds to the firm's starts the person's form empty; the others replace it, so they start from what applies. */
function startingValue<K extends PolicyKey>(key: K, policy: MyPolicy<K>): PolicyValues[K] {
  // A locked form cannot be edited, so it shows what the firm made apply
  if (policy.locked) return policy.resolved;
  if (policy.mine !== null) return policy.mine;
  if (!POLICY_KEYS_ADDING_TO_FIRM.includes(key)) return policy.resolved;
  return (key === 'standing_instructions' ? [] : {}) as PolicyValues[K];
}

function sourceOf(policy: MyPolicy): string {
  if (policy.locked) return 'Set by your firm';
  if (policy.mine !== null) return 'Yours';
  if (policy.firm !== null) return "Your firm's default";
  return 'Agent default';
}

function noteOf<K extends PolicyKey>(key: K, policy: MyPolicy<K>): string | undefined {
  if (policy.locked) return `Your firm set this for everyone: ${describePolicy(key, policy.resolved)}.`;
  if (policy.firm === null) return undefined;
  return `Your firm's default: ${describePolicy(key, policy.firm)}.${POLICY_KEYS_ADDING_TO_FIRM.includes(key) ? ' Anything you set here adds to it.' : ' What you set here replaces it.'}`;
}

export function AgentPreferences() {
  const queryClient = useQueryClient();
  // Read again on every visit and on coming back to the tab: the Chief of Staff saves values from chat and Slack
  const policies = useQuery({ queryKey: MY_POLICIES_QUERY_KEY, queryFn: () => apiFetch<MyPolicies>('/policies/me'), refetchOnMount: 'always', refetchOnWindowFocus: true });
  const refresh = () => queryClient.invalidateQueries({ queryKey: MY_POLICIES_QUERY_KEY });
  // A suggestion arrives from the daily learner, and may have been decided in Slack meanwhile
  const proposal = useQuery({
    queryKey: MY_PROPOSAL_QUERY_KEY,
    queryFn: () => apiFetch<{ proposal: Proposal | null }>('/policies/proposals'),
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
  // The suggestion as the person just decided it, which says what came of their click
  const [decided, setDecided] = useState<Proposal | null>(null);
  const showDecided = (card: Proposal) => {
    setDecided(card);
    void queryClient.invalidateQueries({ queryKey: MY_PROPOSAL_QUERY_KEY });
    void refresh();
  };
  const suggestion = decided ?? proposal.data?.proposal ?? null;

  // A save replaces the whole value, so it says which value it replaces: the server refuses it if that changed since,
  // and the refetch then shows what applies now beside the server's reason
  // A card starts over after its own save or reset succeeds, from what the server holds then
  const [saves, setSaves] = useState<Partial<Record<PolicyKey, number>>>({});
  const startOver = (key: PolicyKey) => setSaves((counts) => ({ ...counts, [key]: (counts[key] ?? 0) + 1 }));

  const save = useMutation({
    mutationFn: ({ key, value, expected }: { key: PolicyKey; value: PolicyValues[PolicyKey]; expected: PolicyValues[PolicyKey] | null }) =>
      apiFetch<void>(`/policies/me/${key}`, { method: 'PUT', body: JSON.stringify({ value, expected }) }),
    onSuccess: (_saved, { key }) => startOver(key),
    onSettled: refresh,
  });
  const reset = useMutation({
    mutationFn: ({ key, expected }: { key: PolicyKey; expected: PolicyValues[PolicyKey] | null }) => apiFetch<void>(`/policies/me/${key}`, { method: 'DELETE', body: JSON.stringify({ expected }) }),
    onSuccess: (_reset, { key }) => startOver(key),
    onSettled: refresh,
  });

  if (policies.isPending) {
    return (
      <Box>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (policies.isError) return <Alert severity="error">Could not load your agent preferences.</Alert>;

  const errorFor = (key: PolicyKey): string | null => {
    if (save.isError && save.variables?.key === key) return save.error.message;
    // A reset refused because the value changed since the page loaded says so, like a save
    if (reset.isError && reset.variables?.key === key) return (reset.error as { status?: number }).status === 409 ? reset.error.message : 'Could not reset this. Try again.';
    return null;
  };

  return (
    <Stack spacing={3}>
      <Typography color="text.secondary" sx={{ maxWidth: 800 }}>
        Every agent that works for you follows these. They change how agents write and when they reach you, never what they may do: anything that changes data still waits for approval.
      </Typography>
      {suggestion && <ProposalCard key={`${suggestion.id}:${suggestion.status}`} card={suggestion} onDecided={showDecided} />}
      {POLICY_KEYS.map((key) => {
        const policy = policies.data[key] as MyPolicy<typeof key>;
        return (
          <PolicyCard
            key={`${key}:${saves[key] ?? 0}`}
            policyKey={key}
            initial={startingValue(key, policy)}
            revision={policy.mine}
            source={sourceOf(policy)}
            note={noteOf(key, policy)}
            disabled={policy.locked}
            saving={(save.isPending && save.variables?.key === key) || (reset.isPending && reset.variables?.key === key)}
            error={errorFor(key)}
            onSave={(value, startedFrom) => save.mutate({ key, value, expected: startedFrom as PolicyValues[PolicyKey] | null })}
            onReset={policy.mine !== null && !policy.locked ? (startedFrom) => reset.mutate({ key, expected: startedFrom as PolicyValues[PolicyKey] | null }) : undefined}
          />
        );
      })}
    </Stack>
  );
}
